import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  signal,
} from '@angular/core';
import { SpecialtyService } from '../../services';
import { SpecialtyModel } from '../../models';

import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { A11yModule } from '@angular/cdk/a11y';
import { AppModal, ModalFooterDirective } from '../../uikit/modal/modal.component';
import { CheckboxComponent } from '../../uikit/checkbox/checkbox.component';
import { AppButton } from '../../uikit/button/button.component';
import { AppToggle, ToggleOption } from '../../uikit/toggle/toggle.component';
import { SearchFiltersStateService } from '../../services/search-filters-state.service';

class SpecialtyViewModel extends SpecialtyModel {
  public isChecked: boolean;
}

@Component({
  selector: 'app-browse-specialties',
  templateUrl: './browse-specialties.component.html',
  styleUrls: ['./browse-specialties.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [A11yModule, AppModal, ModalFooterDirective, AppToggle, CheckboxComponent, AppButton],
})
export class BrowseSpecialtiesComponent implements OnChanges, OnDestroy {
  @Output() public searchClicked = new EventEmitter();
  @Output() public closeModal = new EventEmitter();
  @Output() public specialtiesChange = new EventEmitter();
  @Input() public isModalOpen = false;
  @Input() public width = '800px';

  // NEW: allow parent to control initial/current tab
  @Input()
  set selectedTabInput(value: 'Residencies' | 'Fellowships') {
    this.selectedTab = value ?? 'Residencies';
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKeydown(_event: KeyboardEvent): void {
    this.isModalOpen = false;
  }

  public subscription;

  public residencies = signal<SpecialtyModel[]>([]);
  public fellowships = signal<SpecialtyModel[]>([]);

  public selectedSpecialties = signal<SpecialtyModel[]>([]);
  public get currentlySelected(): number[] {
    return this.specialtyService.getSelectedArray();
  }

  public readonly tabOptions: ToggleOption[] = [
    { value: 'Residencies', label: 'Residency' },
    { value: 'Fellowships', label: 'Fellowships' },
  ];

  public selectedTab = 'Residencies';

  constructor(
    private specialtyService: SpecialtyService,
    private router: Router,
    private searchFiltersState: SearchFiltersStateService,
  ) {}

  public ngOnChanges() {
    if (this.isModalOpen) {
      //execute once modal actually opens
      this.subscription = this.specialtyService
        .getAllSpecialties()
        .pipe(take(1))
        .subscribe((specialties: SpecialtyViewModel[]) => {
          const selectedNids = new Set(this.searchFiltersState.selectedSpecialtyNids());

          this.residencies.set(specialties
            .filter((spec: SpecialtyViewModel) => spec.type === 'Residency')
            .map((spec) => {
              spec.isChecked = selectedNids.has(Number(spec.nid));
              return spec;
            }));
          this.fellowships.set(specialties
            .filter((spec: SpecialtyViewModel) => spec.type === 'Fellowship')
            .map((spec) => {
              spec.isChecked = selectedNids.has(Number(spec.nid));
              return spec;
            }));
          this.selectedSpecialties.set(specialties.filter(
            (spec: SpecialtyViewModel) => spec.isChecked,
          ));
          this.specialtyService.setSelectedSpecs(this.selectedSpecialties());
        });
    }
  }

  public onSpecialtySelected(isChecked: boolean, specialty: SpecialtyModel) {
    const sNid = specialty.nid;
    if (isChecked) {
      this.specialtyService.setSelected(specialty);
    } else {
      this.specialtyService.removeSelected(specialty.nid);
    }
    this.selectedSpecialties.set(this.specialtyService.getSelectedSpecs());
    const spec = this.residencies().concat(this.fellowships()).find((sp) => sp.nid === specialty.nid);
    if (spec && spec.isChecked !== isChecked) {
      spec.isChecked = isChecked;
    }
    this.searchFiltersState.selectedSpecialtyNids.set(
      this.selectedSpecialties().map((s) => Number(s.nid)),
    );
    this.specialtiesChange.emit({ isChecked, sNid });
  }

  public onSearchClicked() {
    const selectedSpecs = this.residencies().concat(this.fellowships()).filter((spec) => {
      if (spec.isChecked) {
        return spec;
      }
    });

    this.specialtyService.setSelectedSpecs(selectedSpecs);
    this.searchClicked.emit({ term: '' });
    this.isModalOpen = false;
  }

  public closeSpecialtiesModal() {
    this.isModalOpen = false;
    this.closeModal.emit({ modal: this.isModalOpen });
  }

  public changeTab(text: string) {
    this.selectedTab = text;
  }

  public closeModel() {
    this.isModalOpen = false;
    this.closeModal.emit();
  }

  public get canSearch(): boolean {
    return this.selectedSpecialties() && this.selectedSpecialties().length > 0;
  }

  public clearSelected() {
    this.specialtyService.clearSelected();
  }

  public ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
